/**
 * Zona di caricamento con trascinamento.
 *
 * Il trascinamento e' un'aggiunta, non l'unico modo: la zona e' un `<button>`
 * che apre la finestra di selezione, quindi funziona da tastiera e con gli
 * screen reader. Il filtro sulle estensioni e' un aiuto all'operatore, non un
 * controllo di sicurezza: la validazione che conta — formato, pagine da
 * scansione, PDF/UA — la fa la pipeline lato server.
 */

import { useRef, useState } from "react";
import type { DragEvent } from "react";
import { pesoLocale } from "../lib/formats";
import {
  ACCETTA_ESTENSIONI,
  ESTENSIONI_AMMESSE,
  estensioneAmmessa,
  estensioneDi,
} from "../lib/documenti";

export function ZonaRilascio({
  onFile,
  disabilitata = false,
}: {
  onFile: (file: File) => void;
  disabilitata?: boolean;
}) {
  const [sopra, setSopra] = useState(false);
  const [avviso, setAvviso] = useState<string | null>(null);
  const campoFile = useRef<HTMLInputElement>(null);

  const accetta = (file: File | undefined) => {
    if (!file) return;
    if (!estensioneAmmessa(file.name)) {
      const estensione = estensioneDi(file.name);
      setAvviso(
        `Formato «${estensione || "sconosciuto"}» non ammesso. Formati accettati: ${ESTENSIONI_AMMESSE.join(", ")}.`,
      );
      return;
    }
    setAvviso(null);
    onFile(file);
  };

  const alTrascinamento = (evento: DragEvent<HTMLElement>, sopraOra: boolean) => {
    evento.preventDefault();
    if (!disabilitata) setSopra(sopraOra);
  };

  return (
    <div className="pila-stretta">
      <button
        type="button"
        className={`rilascio${sopra ? " rilascio--attiva" : ""}`}
        disabled={disabilitata}
        onClick={() => campoFile.current?.click()}
        onDragEnter={(evento) => alTrascinamento(evento, true)}
        onDragOver={(evento) => alTrascinamento(evento, true)}
        onDragLeave={(evento) => alTrascinamento(evento, false)}
        onDrop={(evento) => {
          alTrascinamento(evento, false);
          if (!disabilitata) accetta(evento.dataTransfer.files[0]);
        }}
      >
        <span className="rilascio__titolo">Trascinare qui il file, oppure premere per sceglierlo</span>
        <span className="rilascio__nota">
          Formati ammessi: {ESTENSIONI_AMMESSE.join(" · ")}. JSON e' il formato di riferimento per i
          dati strutturati; i PDF non devono contenere pagine da scansione.
        </span>
      </button>

      <input
        ref={campoFile}
        type="file"
        accept={ACCETTA_ESTENSIONI}
        hidden
        onChange={(evento) => {
          accetta(evento.target.files?.[0]);
          evento.target.value = "";
        }}
      />

      {avviso ? (
        <p className="campo__errore" role="alert">
          {avviso}
        </p>
      ) : null}
    </div>
  );
}

/** Riepilogo del file selezionato, prima dell'invio. */
export function FileScelto({ file, onRimuovi }: { file: File; onRimuovi?: () => void }) {
  return (
    <div className="file-scelto">
      <span className="file-scelto__estensione" aria-hidden="true">
        {estensioneDi(file.name) || "?"}
      </span>
      <div className="file-scelto__corpo">
        <div className="file-scelto__nome">{file.name}</div>
        <div className="file-scelto__meta">
          {pesoLocale(file.size)} · anteprima locale, il peso definitivo lo calcola l'API
        </div>
      </div>
      {onRimuovi ? (
        <button type="button" className="bottone bottone--fantasma bottone--piccolo" onClick={onRimuovi}>
          Rimuovi
        </button>
      ) : null}
    </div>
  );
}
